import { useEffect } from 'react';
import { useFilteredContextswithResources } from '../../../../hooks/useFilteredContextsWithResources';
import {
  SitecoreContextType,
  ExtendedSitecoreContextType,
  FilteredContextWithResources,
  SitecoreContextResourceType,
} from '../../../../types/types';

// Transform FilteredContextWithResources to ExtendedSitecoreContextType
const transformScopedContext = (
  scopedContext: FilteredContextWithResources,
  parentContext: ExtendedSitecoreContextType
): ExtendedSitecoreContextType => {
  return {
    sitecoreContextId: scopedContext.sitecoreContextId,
    name: scopedContext.name,
    description: '', // Scoped contexts do not have description
    organizationId: parentContext.organizationId,
    type: 'scoped',
    accountId: parentContext.accountId,
    resources: scopedContext.resources || [],
    masterSitecoreContextId: scopedContext.masterSitecoreContextId,
    isScopedContext: true,
    createdAt: scopedContext.createdAt,
    updatedAt: scopedContext.updatedAt,
  };
};

interface ScopedContextLoadersProps {
  expandedRows: Set<string>;
  rawTableData: SitecoreContextType[];
  loadedScopedContexts: Map<string, ExtendedSitecoreContextType[]>;
  setErrorScopedContexts: React.Dispatch<React.SetStateAction<Map<string, Error>>>;
  setLoadedScopedContexts: React.Dispatch<React.SetStateAction<Map<string, ExtendedSitecoreContextType[]>>>;
  setExpandedRows: React.Dispatch<React.SetStateAction<Set<string>>>;
  setRefetchFunctions: React.Dispatch<React.SetStateAction<Map<string, () => void>>>;
}

export const ScopedContextLoaders = ({
  expandedRows,
  rawTableData,
  loadedScopedContexts,
  setErrorScopedContexts,
  setLoadedScopedContexts,
  setExpandedRows,
  setRefetchFunctions,
}: ScopedContextLoadersProps) => {
  return (
    <>
      {Array.from(expandedRows).map((contextId) => {
        const context = rawTableData.find((ctx: SitecoreContextType) => ctx.sitecoreContextId === contextId);
        if (!context || loadedScopedContexts.has(contextId)) {
          return null;
        }
        return (
          <ScopedContextLoader
            key={contextId}
            contextId={contextId}
            resources={context.resources || []}
            rawTableData={rawTableData}
            setErrorScopedContexts={setErrorScopedContexts}
            setLoadedScopedContexts={setLoadedScopedContexts}
            setExpandedRows={setExpandedRows}
            setRefetchFunctions={setRefetchFunctions}
          />
        );
      })}
    </>
  );
};

interface ScopedContextLoaderProps {
  contextId: string;
  resources: SitecoreContextResourceType[];
  rawTableData: SitecoreContextType[];
  setErrorScopedContexts: React.Dispatch<React.SetStateAction<Map<string, Error>>>;
  setLoadedScopedContexts: React.Dispatch<React.SetStateAction<Map<string, ExtendedSitecoreContextType[]>>>;
  setExpandedRows: React.Dispatch<React.SetStateAction<Set<string>>>;
  setRefetchFunctions: React.Dispatch<React.SetStateAction<Map<string, () => void>>>;
}

const ScopedContextLoader = ({
  contextId,
  resources,
  rawTableData,
  setErrorScopedContexts,
  setLoadedScopedContexts,
  setExpandedRows,
  setRefetchFunctions,
}: ScopedContextLoaderProps) => {
  const { filteredContextsWithResources, isLoading, error, refetch } = useFilteredContextswithResources({
    sitecoreContextId: contextId,
    resources,
  });

  // Store refetch function so parent can call it on retry
  useEffect(() => {
    setRefetchFunctions(prev => {
      const newMap = new Map(prev);
      newMap.set(contextId, refetch);
      return newMap;
    });

    return () => {
      setRefetchFunctions(prev => {
        const newMap = new Map(prev);
        newMap.delete(contextId);
        return newMap;
      });
    };
  }, [contextId, refetch, setRefetchFunctions]);

  useEffect(() => {
    if (error) {
      setErrorScopedContexts(prev => {
        const newMap = new Map(prev);
        newMap.set(contextId, error as Error);
        return newMap;
      });
    } else {
      // Clear error state when not in error
      setErrorScopedContexts(prev => {
        const newMap = new Map(prev);
        newMap.delete(contextId);
        return newMap;
      });
    }
  }, [error, contextId, setErrorScopedContexts]);

  useEffect(() => {
    if (!isLoading && !error) {
      const parentContext = (rawTableData as SitecoreContextType[]).find((ctx: SitecoreContextType) => ctx.sitecoreContextId === contextId);
      let transformed: ExtendedSitecoreContextType[] = [];
      
      if (parentContext && filteredContextsWithResources.length > 0) {
        transformed = filteredContextsWithResources.map(sc => 
          transformScopedContext(sc, parentContext as ExtendedSitecoreContextType)
        );
      }

      setLoadedScopedContexts(prev => {
        const newMap = new Map(prev);
        newMap.set(contextId, transformed);
        return newMap;
      });

      // Ensure row is expanded after data loads (although contextId should already be in Set from expand click)
      setExpandedRows(prev => {
        if (!prev.has(contextId)) {
          return new Set(prev).add(contextId);
        }
        return prev;
      });
    }
  }, [filteredContextsWithResources, isLoading, error, contextId, rawTableData, setLoadedScopedContexts, setExpandedRows]);

  return null;
};
